import React, { useEffect, useState, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useNavigateWithNewTab } from '../hooks/useNavigateWithNewTab';
import { Plus, RotateCcw, ChevronLeft, ChevronRight } from 'lucide-react';
import { useStore } from '../store';
import { useShallow } from 'zustand/react/shallow';
import {
  Table,
  TableBody,
  TableHead,
  TableHeader,
  TableRow,
  Button,
  Card,
  CardContent,
  Input,
  Alert,
  AlertDescription,
  Select,
  SelectTrigger,
  SelectContent,
  SelectItem,
  SelectValue,
  Checkbox,
} from '@roger/ui-components';
import { FileText, AlertCircle, Loader2, AlertTriangle } from 'lucide-react';
import {
  useIntake,
  type IntakeFilters,
  type PaginatedIntakesResult,
} from '../hooks/useIntake';
import { useEnabledTabs } from '../hooks/useEnabledTabs';
import type { IntakeRecord } from '../store/slices/intakeSlice';

import NewIntakeDialog from '../components/NewIntakeDialog';
import IntakeRowWithStatus from '../components/IntakeRowWithStatus';
import IntakeStatusFilter, {
  type StatusFilterType,
} from '../components/IntakeStatusFilter';
import FailedIntakeDrawer from '../components/FailedIntakeDrawer';
import RerunIntakeDialog from '../components/RerunIntakeDialog';
import IntakeSelectionActions from '../components/IntakeSelectionActions';

const DEFAULT_PER_PAGE = 20;

const IntakeManager: React.FC = () => {
  const {
    hasPermission,
    updateIntake: updateIntakeInStore,
    agencyInsurancesLoading,
    fetchAgencyInsurances,
    shouldRefreshAgencyInsurances,
    intakeManagerFilters,
    setIntakeManagerFilters,
    agencyClinics,
    agencyClinicsLoading,
    fetchAgencyClinics,
    user,
  } = useStore(
    useShallow(state => ({
      hasPermission: state.hasPermission,
      updateIntake: state.updateIntake,
      agencyInsurancesLoading: state.agencyInsurancesLoading,
      fetchAgencyInsurances: state.fetchAgencyInsurances,
      shouldRefreshAgencyInsurances: state.shouldRefreshAgencyInsurances,
      intakeManagerFilters: state.intakeManagerFilters,
      setIntakeManagerFilters: state.setIntakeManagerFilters,
      agencyClinics: state.agencyClinics,
      agencyClinicsLoading: state.agencyClinicsLoading,
      fetchAgencyClinics: state.fetchAgencyClinics,
      user: state.user,
    }))
  );

  const enabledTabs = useEnabledTabs();
  const {
    intakes,
    intakesLoading,
    intakesError,
    loadIntakes,
    loadIntakeDetails,
  } = useIntake();

  // Filter states - initialize from persisted store
  const [searchTerm, setSearchTerm] = useState('');
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState('');
  const [statusFilters, setStatusFilters] = useState<StatusFilterType[]>(
    (intakeManagerFilters?.statusFilters ?? []) as StatusFilterType[]
  );
  const [clinicFilter, setClinicFilter] = useState<string>('all');
  const [startDate, setStartDate] = useState<string>('');
  const [endDate, setEndDate] = useState<string>('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationInfo, setPaginationInfo] = useState<PaginatedIntakesResult>({
    items: [],
    total: 0,
    page: 1,
    perPage: DEFAULT_PER_PAGE,
    totalPages: 1,
  });

  const [showNewIntakeDialog, setShowNewIntakeDialog] = useState(false);
  const navigate = useNavigate();
  const navigateTo = useNavigateWithNewTab();

  // Rerun modal state
  const [rerunModalOpen, setRerunModalOpen] = useState(false);
  const [intakeToRerun, setIntakeToRerun] = useState<IntakeRecord | null>(null);

  // Failed intake drawer state
  const [failedDrawerOpen, setFailedDrawerOpen] = useState(false);
  const [selectedFailedIntake, setSelectedFailedIntake] =
    useState<IntakeRecord | null>(null);

  // Selection state
  const [selectedIntakeIds, setSelectedIntakeIds] = useState<string[]>([]);

  // Track if this is initial mount
  const isInitialMount = useRef(true);
  const hasFetchedAgencyInsurancesRef = React.useRef(false);
  const hasFetchedAgencyClinicsRef = React.useRef(false);

  // Debounce search term
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm);
    }, 300); // 300ms debounce

    return () => clearTimeout(timer);
  }, [searchTerm]);

  // Persist status filters to store when they change
  useEffect(() => {
    setIntakeManagerFilters({ statusFilters });
  }, [statusFilters, setIntakeManagerFilters]);

  // Check permission - redirect if user doesn't have intake:access
  useEffect(() => {
    if (!hasPermission('intake:access')) {
      navigate('/dashboard');
    }
  }, [hasPermission, navigate]);

  // Fetch agency insurances on component mount (once, globally cached)
  useEffect(() => {
    if (
      !hasFetchedAgencyInsurancesRef.current &&
      shouldRefreshAgencyInsurances() &&
      !agencyInsurancesLoading
    ) {
      hasFetchedAgencyInsurancesRef.current = true;
      fetchAgencyInsurances();
    }
  }, [
    shouldRefreshAgencyInsurances,
    agencyInsurancesLoading,
    fetchAgencyInsurances,
  ]);

  // Fetch agency clinics on component mount
  useEffect(() => {
    if (
      !hasFetchedAgencyClinicsRef.current &&
      agencyClinics.length === 0 &&
      !agencyClinicsLoading
    ) {
      hasFetchedAgencyClinicsRef.current = true;
      fetchAgencyClinics();
    }
  }, [agencyClinics.length, agencyClinicsLoading, fetchAgencyClinics]);

  // Check if user has permission to access Intake
  if (!hasPermission('intake:access')) {
    return (
      <Alert className='border-red-200 bg-red-50'>
        <AlertDescription className='text-red-800 flex gap-1 items-center'>
          <AlertTriangle className='h-4 w-4 text-red-600' />
          You don't have permission to access Intake Manager features. Please
          contact your administrator if you need access.
        </AlertDescription>
      </Alert>
    );
  }

  // Load intakes when filters change
  const fetchIntakes = useCallback(async () => {
    const filters: IntakeFilters = {
      search: debouncedSearchTerm || undefined,
      startDate: startDate || undefined,
      endDate: endDate || undefined,
      statuses: statusFilters.length > 0 ? statusFilters : undefined,
      clinicId: clinicFilter !== 'all' ? clinicFilter : undefined,
      page: currentPage,
      perPage: DEFAULT_PER_PAGE,
    };

    const result = await loadIntakes(filters);
    setPaginationInfo(result);
  }, [
    loadIntakes,
    debouncedSearchTerm,
    startDate,
    endDate,
    statusFilters,
    clinicFilter,
    currentPage,
  ]);

  // Initial load and when filters change
  useEffect(() => {
    fetchIntakes();
  }, [fetchIntakes]);

  // Reset to page 1 when search/filter changes (but not on initial mount)
  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }
    setCurrentPage(1);
    setSelectedIntakeIds([]);
  }, [debouncedSearchTerm, statusFilters, clinicFilter, startDate, endDate]);

  // Reset all filters to default
  const handleResetFilters = useCallback(() => {
    setSearchTerm('');
    setDebouncedSearchTerm('');
    setStatusFilters([]);
    setClinicFilter('all');
    setStartDate('');
    setEndDate('');
    setCurrentPage(1);
    setSelectedIntakeIds([]);
  }, []);

  // Pagination handlers
  const handlePreviousPage = useCallback(() => {
    setCurrentPage(prev => Math.max(1, prev - 1));
  }, []);

  const handleNextPage = useCallback(() => {
    setCurrentPage(prev => Math.min(paginationInfo.totalPages, prev + 1));
  }, [paginationInfo.totalPages]);

  const formatDate = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    return dateObj.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  };

  const formatDateTime = (date: Date | string | undefined) => {
    if (!date) return 'N/A';
    const dateObj = date instanceof Date ? date : new Date(date);
    if (isNaN(dateObj.getTime())) {
      return 'N/A';
    }
    return dateObj.toLocaleString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getPatientName = (intake: IntakeRecord) => {
    const firstName = intake.patient_first_name || '';
    const lastName = intake.patient_last_name || '';
    return firstName || lastName ? `${firstName} ${lastName}`.trim() : 'N/A';
  };

  const isProcessing = (intake: IntakeRecord) => {
    // Don't show as processing if already processed or has errors
    if (intake.is_processed || intake.processing_errors) {
      return false;
    }

    // Check if intake is in "received" status and missing key data
    if (intake.status !== 'received') {
      return false;
    }

    // If explicitly marked as processing, show as processing
    if (intake.processing_status === 'processing') {
      return true;
    }

    // Check if key patient information is missing
    const hasPatientName =
      intake.patient_first_name || intake.patient_last_name;
    const hasInsurance = !!intake.agency_insurance;
    const hasReferringProvider = intake.referring_physician;

    // If any of these key fields are missing, consider it processing
    return !hasPatientName || !hasInsurance || !hasReferringProvider;
  };

  const isFailed = (intake: IntakeRecord) => {
    return intake.processing_status === 'failed' && !!intake.processing_errors;
  };

  const handleNewIntakeCreated = () => {
    // Close the dialog immediately
    setShowNewIntakeDialog(false);
    // Refresh intakes to show the newly uploaded intake
    fetchIntakes();
    // No need to call pollIntakeStatus - IntakeRowWithStatus will handle workflow polling
  };

  const handleIntakeWorkflowComplete = async (intakeId: string) => {
    // Refresh only this specific intake instead of reloading all intakes
    console.log('[IntakeManager] Refreshing single intake:', intakeId);
    try {
      const updatedIntake = await loadIntakeDetails(intakeId);
      // Update the intake in the intakes array
      updateIntakeInStore(intakeId, updatedIntake);
    } catch (error) {
      console.error('[IntakeManager] Failed to refresh intake:', error);
    }
  };

  // Rerun handlers
  const handleRerunClick = (intake: IntakeRecord) => {
    setIntakeToRerun(intake);
    setRerunModalOpen(true);
  };

  const handleRerunSuccess = () => {
    setIntakeToRerun(null);
    // Close the drawer if it was open
    setFailedDrawerOpen(false);
    setSelectedFailedIntake(null);
    // Refresh intakes list
    fetchIntakes();
  };

  // Failed intake drawer handlers
  const handleFailedIntakeClick = (intake: IntakeRecord) => {
    setSelectedFailedIntake(intake);
    setFailedDrawerOpen(true);
  };

  const handleRetryFromDrawer = () => {
    if (selectedFailedIntake) {
      handleRerunClick(selectedFailedIntake);
    }
  };

  // Selection handlers
  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      setSelectedIntakeIds(intakes.map(intake => intake.id));
    } else {
      setSelectedIntakeIds([]);
    }
  };

  const handleSelectIntake = (intakeId: string, checked: boolean) => {
    if (checked) {
      setSelectedIntakeIds(prev => [...prev, intakeId]);
    } else {
      setSelectedIntakeIds(prev => prev.filter(id => id !== intakeId));
    }
  };

  const handleClearSelection = () => {
    setSelectedIntakeIds([]);
  };

  return (
    <div className='space-y-6'>
      {/* Header and New Intake Button */}
      <div className='flex justify-between items-center'>
        <div>
          <h1 className='text-2xl font-bold tracking-tight'>Intake Manager</h1>
        </div>
        <Button onClick={() => setShowNewIntakeDialog(true)}>
          <Plus className='h-4 w-4' />
          New Intake
        </Button>
      </div>

      {/* Error Display */}
      {intakesError && (
        <Card className='border-destructive bg-destructive/10'>
          <CardContent className='p-4'>
            <div className='flex items-center gap-2 text-destructive'>
              <AlertCircle className='h-4 w-4' />
              <span className='font-medium'>Error loading intakes</span>
            </div>
            <p className='text-sm text-destructive/80 mt-1'>{intakesError}</p>
            <Button
              variant='outline'
              size='sm'
              className='mt-2'
              onClick={() => fetchIntakes()}
            >
              Try Again
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Filter Controls */}
      <div className='flex flex-wrap gap-4 items-end'>
        {/* Search */}
        <div className='flex-1 min-w-[200px]'>
          <label htmlFor='search' className='text-sm font-medium mb-1.5 block'>
            Search
          </label>
          <Input
            id='search'
            placeholder='Search by patient name, intake ID, insurance, provider, or referral source...'
            value={searchTerm}
            onChange={e => setSearchTerm(e.target.value)}
          />
        </div>

        {/* Start Date */}
        <div className='w-40'>
          <label
            htmlFor='startDate'
            className='text-sm font-medium mb-1.5 block'
          >
            Start Date
          </label>
          <Input
            id='startDate'
            type='date'
            value={startDate}
            onChange={e => setStartDate(e.target.value)}
          />
        </div>

        {/* End Date */}
        <div className='w-40'>
          <label htmlFor='endDate' className='text-sm font-medium mb-1.5 block'>
            End Date
          </label>
          <Input
            id='endDate'
            type='date'
            value={endDate}
            onChange={e => setEndDate(e.target.value)}
          />
        </div>

        {/* Status Filter */}
        <div className='w-56'>
          <label htmlFor='status' className='text-sm font-medium mb-1.5 block'>
            Status
          </label>
          <IntakeStatusFilter
            value={statusFilters}
            onChange={setStatusFilters}
            enabledTabs={enabledTabs}
          />
        </div>

        {/* Clinic Filter - only show when there are multiple clinics */}
        {agencyClinics.length > 1 && (
          <div className='w-48'>
            <label
              htmlFor='clinic'
              className='text-sm font-medium mb-1.5 block'
            >
              Clinic
            </label>
            <Select value={clinicFilter} onValueChange={setClinicFilter}>
              <SelectTrigger id='clinic'>
                <SelectValue placeholder='Filter by clinic' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value='all'>All Clinics</SelectItem>
                {agencyClinics.map(clinic => (
                  <SelectItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        {/* Reset Button */}
        <Button
          variant='outline'
          onClick={handleResetFilters}
          className='flex items-center gap-2'
        >
          <RotateCcw className='h-4 w-4' />
          Reset
        </Button>
      </div>

      {intakesLoading ? (
        <div className='flex items-center justify-center py-16'>
          <Loader2 className='h-8 w-8 animate-spin text-muted-foreground' />
          <span className='ml-3 text-muted-foreground'>Loading intakes...</span>
        </div>
      ) : intakes.length === 0 ? (
        <div className='text-center text-muted-foreground py-8'>
          <FileText className='h-12 w-12 mx-auto mb-4 text-gray-300' />
          <p>No intake records found</p>
          <p className='text-sm'>
            {searchTerm || statusFilters.length > 0 || startDate || endDate
              ? 'Try adjusting your search or filters'
              : 'Upload a document to get started'}
          </p>
        </div>
      ) : (
        <div className='space-y-4'>
          {/* Processing Summary */}
          {intakes.some(intake => isProcessing(intake)) && (
            <div className='flex items-center gap-2 text-sm text-blue-600 bg-blue-50 p-3 rounded-md'>
              <Loader2 className='h-4 w-4 animate-spin' />
              <span>
                {intakes.filter(intake => isProcessing(intake)).length}{' '}
                intake(s) currently processing
              </span>
            </div>
          )}

          <div className='rounded-md border'>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className='w-12'>
                    <Checkbox
                      checked={
                        intakes.length > 0 &&
                        selectedIntakeIds.length === intakes.length
                      }
                      onCheckedChange={handleSelectAll}
                    />
                  </TableHead>
                  <TableHead>Patient</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Clinic</TableHead>
                  <TableHead>Insurance</TableHead>
                  <TableHead>Referring Provider</TableHead>
                  <TableHead>Referral Source</TableHead>
                  <TableHead>Assignee</TableHead>
                  <TableHead>SOC</TableHead>
                  <TableHead>Created</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {intakes.map(intake => (
                  <IntakeRowWithStatus
                    key={intake.id}
                    intake={intake}
                    isProcessing={isProcessing(intake)}
                    getPatientName={getPatientName}
                    formatDate={formatDate}
                    formatDateTime={formatDateTime}
                    agencyClinics={agencyClinics}
                    agencyName={user?.agency?.written_name}
                    onClick={e => {
                      // Don't navigate if processing
                      if (isProcessing(intake)) {
                        return;
                      }
                      // Open drawer for failed intakes instead of navigating
                      if (isFailed(intake)) {
                        handleFailedIntakeClick(intake);
                        return;
                      }
                      navigateTo(`/intake/${intake.id}`)(e);
                    }}
                    onWorkflowComplete={handleIntakeWorkflowComplete}
                    onAssignComplete={() =>
                      handleIntakeWorkflowComplete(intake.id)
                    }
                    enabledTabs={enabledTabs}
                    onRerun={handleRerunClick}
                    showCheckbox={true}
                    isSelected={selectedIntakeIds.includes(intake.id)}
                    onCheckboxChange={checked =>
                      handleSelectIntake(intake.id, checked)
                    }
                  />
                ))}
              </TableBody>
            </Table>
          </div>

          {/* Pagination Controls */}
          <div className='flex items-center justify-between border-t pt-4'>
            <div className='text-sm text-muted-foreground'>
              Showing {(currentPage - 1) * DEFAULT_PER_PAGE + 1} to{' '}
              {Math.min(currentPage * DEFAULT_PER_PAGE, paginationInfo.total)}{' '}
              of {paginationInfo.total} intakes
            </div>
            <div className='flex items-center gap-2'>
              <Button
                variant='outline'
                size='sm'
                onClick={handlePreviousPage}
                disabled={currentPage === 1}
              >
                <ChevronLeft className='h-4 w-4' />
                Previous
              </Button>
              <div className='text-sm px-2'>
                Page {currentPage} of {paginationInfo.totalPages}
              </div>
              <Button
                variant='outline'
                size='sm'
                onClick={handleNextPage}
                disabled={currentPage >= paginationInfo.totalPages}
              >
                Next
                <ChevronRight className='h-4 w-4' />
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* New Intake Dialog */}
      <NewIntakeDialog
        open={showNewIntakeDialog}
        onOpenChange={setShowNewIntakeDialog}
        onIntakeCreated={handleNewIntakeCreated}
      />

      {/* Rerun Intake Dialog */}
      <RerunIntakeDialog
        open={rerunModalOpen}
        onOpenChange={setRerunModalOpen}
        intake={intakeToRerun}
        onSuccess={handleRerunSuccess}
        getPatientName={getPatientName}
      />

      {/* Failed Intake Drawer */}
      <FailedIntakeDrawer
        open={failedDrawerOpen}
        onOpenChange={setFailedDrawerOpen}
        intake={selectedFailedIntake}
        onRetry={handleRetryFromDrawer}
      />

      {/* Selection Actions Bar */}
      {selectedIntakeIds.length > 0 && (
        <IntakeSelectionActions
          selectedIntakes={intakes.filter(intake =>
            selectedIntakeIds.includes(intake.id)
          )}
          onClearSelection={handleClearSelection}
          onAssignComplete={fetchIntakes}
        />
      )}
    </div>
  );
};

export default IntakeManager;
